import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import { API } from "@/App";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Eye,
  Share2,
  BookOpen,
  Calculator,
  FlaskConical,
  BookText,
  Globe2,
  Lightbulb,
  RefreshCw,
  Twitter,
  Linkedin,
  Link2,
  Check,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

// Subject icons mapping
const subjectIcons = {
  mathematics: Calculator,
  science: FlaskConical,
  english: BookText,
  hindi: BookOpen,
  social_studies: Globe2,
  general: Lightbulb,
};

// Subject colors mapping
const subjectColors = {
  mathematics: {
    badge: "bg-blue-100 text-blue-700 border-blue-200",
    accent: "text-blue-600",
    bg: "from-blue-50 to-indigo-50",
  },
  science: {
    badge: "bg-green-100 text-green-700 border-green-200",
    accent: "text-green-600",
    bg: "from-green-50 to-emerald-50",
  },
  english: {
    badge: "bg-purple-100 text-purple-700 border-purple-200",
    accent: "text-purple-600",
    bg: "from-purple-50 to-violet-50",
  },
  hindi: {
    badge: "bg-orange-100 text-orange-700 border-orange-200",
    accent: "text-orange-600",
    bg: "from-orange-50 to-amber-50",
  },
  social_studies: {
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    accent: "text-amber-600",
    bg: "from-amber-50 to-yellow-50",
  },
  general: {
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    accent: "text-emerald-600",
    bg: "from-emerald-50 to-teal-50",
  },
};

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API}/blog/posts/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data.post);
      } else if (response.status === 404) {
        setError("Article not found");
      } else {
        setError("Failed to load article");
      }
    } catch (err) {
      console.error("Error fetching post:", err);
      setError("Failed to load article");
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = post?.title || "Eduzy Learning Blog";

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    };

    if (urls[platform]) {
      window.open(urls[platform], "_blank", "width=600,height=400");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <SharedHeader />
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
        <SharedFooter />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <SharedHeader />
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <BookOpen className="w-16 h-16 text-gray-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error || "Article Not Found"}
          </h1>
          <p className="text-gray-600 mb-6">
            The article you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Button onClick={() => navigate("/blog")} className="bg-emerald-600 hover:bg-emerald-700">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
        </div>
        <SharedFooter />
      </div>
    );
  }

  const SubjectIcon = subjectIcons[post.subject] || BookOpen;
  const colors = subjectColors[post.subject] || subjectColors.general;
  const formattedDate = new Date(post.created_at).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // SEO structured data for blog article
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt,
        "datePublished": post.created_at,
        "dateModified": post.updated_at || post.created_at,
        "author": {
          "@type": "Organization",
          "name": "Eduzy AI",
        },
        "publisher": {
          "@type": "Organization",
          "name": "Eduzy",
          "logo": {
            "@type": "ImageObject",
            "url": "https://eduzy.co/eduzy-logo-transparent.png",
          },
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://eduzy.co/blog/${post.slug}`,
        },
      },
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title, url: `/blog/${post.slug}` },
      ]),
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SEO
        title={`${post.title} | Eduzy Learning Blog`}
        description={post.excerpt}
        keywords={`${post.subject}, education, learning, grades 5-8, ${post.title.toLowerCase()}`}
        canonicalUrl={`/blog/${post.slug}`}
        structuredData={articleStructuredData}
      />

      <SharedHeader />

      {/* Article Header */}
      <section className={`bg-gradient-to-br ${colors.bg} py-8 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/blog")}
            className="mb-6 text-gray-600 hover:text-gray-900"
            data-testid="back-to-blog-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>

          {/* Subject Badge */}
          <Badge className={`${colors.badge} border mb-4`}>
            <SubjectIcon className="w-3 h-3 mr-1" />
            {post.subject?.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </Badge>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="article-title">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formattedDate}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.read_time || "1 min"} read
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {post.views || 0} views
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Content */}
          <article className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-8">
            <div 
              className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
              data-testid="article-content"
            >
              {post.content.split("\n").map((paragraph, idx) => (
                paragraph.trim() && (
                  <p key={idx} className="mb-4">
                    {paragraph}
                  </p>
                )
              ))}
            </div>

            {/* AI Generated Badge */}
            {post.ai_generated && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <span>This article was generated by Eduzy AI to help students learn.</span>
                </div>
              </div>
            )}
          </article>

          {/* Share Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-900">Share this article</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("whatsapp")}
                  className="text-green-600 border-green-200 hover:bg-green-50"
                  data-testid="share-whatsapp"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("twitter")}
                  className="text-sky-500 border-sky-200 hover:bg-sky-50"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("linkedin")}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyLink}
                  className={copied ? "text-green-600 border-green-200 bg-green-50" : ""}
                  data-testid="copy-link"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-3xl mx-auto text-center">
          <GraduationCap className="w-10 h-10 text-white/80 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-white mb-3">
            Want to Learn More?
          </h2>
          <p className="text-emerald-100 mb-5">
            Our AI tutor can help you understand this topic better with personalized lessons!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/eduwise")}
              size="lg"
              className="bg-white text-emerald-600 hover:bg-gray-100"
              data-testid="cta-eduwise"
            >
              Try EduWise AI
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate("/blog")}
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              data-testid="cta-more-articles"
            >
              More Articles
            </Button>
          </div>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}
